import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SEO from '@/components/SEO';
import { auth } from '@/utils/api';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'buyer',
    name: '',
    phone: '',
    gstNumber: '',
    panNumber: '',
    bio: '',
    category: '',
    profileImage: null
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.userType === 'artist') {
      if (!formData.category) {
        toast.error('Please select your artist category');
        return;
      }
      if (!formData.profileImage) {
        toast.error('Please upload a profile image or sample work');
        return;
      }
      if (!formData.bio || formData.bio.trim().length === 0) {
        toast.error('Please write your artist profile');
        return;
      }
    }

    setLoading(true);

    try {
      const { confirmPassword, profileImage, ...registerData } = formData;
      
      // Always use FormData (backend expects Form data, not JSON)
      const formDataToSend = new FormData();
      Object.keys(registerData).forEach(key => {
        if (registerData[key] !== null && registerData[key] !== undefined) {
          formDataToSend.append(key, registerData[key]);
        }
      });
      
      // Add profile image for artists
      if (formData.userType === 'artist' && profileImage) {
        formDataToSend.append('profileImage', profileImage);
      }
      
      const response = await auth.registerWithImage(formDataToSend);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Registration successful!');
      
      if (formData.userType === 'artist') {
        toast.info('Your account is pending admin approval');
        navigate('/artist-dashboard');
      } else {
        toast.success('Welcome! Start exploring beautiful artworks');
        navigate('/products');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Registration failed';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: '#ffffff'}} data-testid="register-page">
      <SEO 
        title="Register"
        description="Create your ARTCARDO account. Join as an art buyer or register as an artist to sell your unique handmade artworks."
        url="/register"
        noindex={true}
      />
      {/* Black header section with logo */}
      <div style={{background: '#000000', padding: '2rem 0', textAlign: 'center'}}>
        <Link to="/" className="inline-block" data-testid="logo-link-header">
          <img 
            src="/artcardo-logo.png" 
            alt="ARTCARDO Logo" 
            style={{height: '150px', width: 'auto', margin: '0 auto'}}
          />
        </Link>
      </div>

      {/* White content section */}
      <div className="py-12">
        <div className="card max-w-2xl mx-4 md:mx-auto" style={{border: '2px solid #000000'}}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Create Your Account</h2>
            <p className="text-gray-600" data-testid="register-subtitle">Join our community of artists and art lovers</p>
          </div>

        <form onSubmit={handleSubmit} data-testid="register-form">
          <div className="form-group">
            <label className="form-label">I am a</label>
            <Select value={formData.userType} onValueChange={(value) => setFormData({ ...formData, userType: value })}>
              <SelectTrigger data-testid="usertype-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="buyer">Buyer (Art Lover)</SelectItem>
                <SelectItem value="artist">Artist/Artisan (Seller)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="form-input"
                data-testid="name-input"
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="form-input"
                data-testid="phone-input"
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="form-input"
              data-testid="email-input"
              placeholder="Enter your email address"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Password</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="form-input"
                data-testid="password-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="form-input"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          {formData.userType === 'artist' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">GST Number (Optional)</label>
                  <Input
                    type="text"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                    className="form-input"
                    data-testid="gst-input"
                    placeholder="Enter GST number if applicable"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PAN Number (Optional)</label>
                  <Input
                    type="text"
                    value={formData.panNumber}
                    onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                    className="form-input"
                    data-testid="pan-input"
                    placeholder="Enter PAN number if applicable"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Artist Profile (200 words max) *</label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/).filter(word => word.length > 0);
                    if (words.length <= 200) {
                      setFormData({ ...formData, bio: e.target.value });
                    }
                  }}
                  required
                  className="form-textarea"
                  rows={4}
                  data-testid="bio-textarea"
                  placeholder="Tell us about yourself as an artist, your style, inspiration, and experience..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.bio ? formData.bio.trim().split(/\s+/).filter(word => word.length > 0).length : 0}/200 words
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Artist Category *</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="form-input"
                  data-testid="category-select"
                >
                  <option value="">Select your art category</option>
                  <option value="Painting">Painting</option>
                  <option value="Sculpture">Sculpture</option>
                  <option value="Photography">Photography</option>
                  <option value="Digital Art">Digital Art</option>
                  <option value="Crafts">Crafts & Handmade</option>
                  <option value="Textile">Textile Art</option>
                  <option value="Pottery">Pottery & Ceramics</option>
                  <option value="Jewelry">Jewelry Making</option>
                  <option value="Woodwork">Woodwork</option>
                  <option value="Mixed Media">Mixed Media</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Profile Image or Sample Work * (for approval evaluation)</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, profileImage: e.target.files[0] })}
                  required
                  className="form-input"
                  data-testid="profile-image-input"
                />
                <p className="text-xs text-gray-500 mt-1">Upload your profile photo or a sample of your artwork</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">Artist Registration</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Easy Registration:</strong> Simple signup process to get started</li>
                  <li><strong>Secure Platform:</strong> All transactions processed securely through our payment gateway</li>
                  <li><strong>Approval Required:</strong> Your account requires admin approval before listing products</li>
                  <li><strong>Terms & Conditions:</strong> You'll review and accept our terms after account approval</li>
                </ul>
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full mb-4"
            style={{background: '#ef4444'}}
            data-testid="register-btn"
          >
            {loading ? 'Creating Account...' : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Create Account
              </>
            )}
          </Button>
        </form>

        <div className="text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#ef4444] font-semibold hover:underline" data-testid="login-link">
              Login here
            </Link>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Register;